import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useNotification } from '../contexts/NotificationContext';

function scannedTables(tables) {
  return (tables || []).filter((t) => t && t.scanStatus !== 'removed');
}

async function errorMessageFromAxios(err) {
  const data = err.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    const text = await data.text();
    try {
      const parsed = JSON.parse(text);
      if (parsed?.error) return parsed.error;
    } catch {
      /* el cuerpo no era JSON */
    }
    return text || err.message;
  }
  return data?.error || err.message || 'Error al exportar';
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function filenameFromDisposition(header, fallback) {
  if (!header) return fallback;
  const utf = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf?.[1]) {
    try {
      return decodeURIComponent(utf[1].trim());
    } catch {
      return fallback;
    }
  }
  const ascii = header.match(/filename="?([^";]+)"?/i);
  return ascii?.[1]?.trim() || fallback;
}

const ExcelTemplateExportDialog = ({
  open,
  onClose,
  catalog,
  getAuthHeaders,
}) => {
  const { showSuccess, showError } = useNotification();
  const tables = useMemo(() => scannedTables(catalog?.tables), [catalog]);
  const [tableId, setTableId] = useState('');
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (!open) return;
    setTableId('');
    setSelected([]);
  }, [open]);

  const selectedTable = useMemo(
    () => tables.find((t) => t.id === tableId) || null,
    [tables, tableId]
  );
  const columns = selectedTable?.columns || [];

  const handleTableChange = (nextId) => {
    setTableId(nextId || '');
    setSelected([]);
  };

  const toggleColumn = (name) => {
    setSelected((prev) =>
      prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]
    );
  };

  const allSelected = columns.length > 0 && selected.length === columns.length;

  const exportMutation = useMutation({
    mutationFn: async () => {
      const headers = getAuthHeaders?.() || {};
      return axios.post(
        '/api/admin/data-model/excel-template',
        {
          connectionId: catalog.connectionId,
          database: catalog.database,
          tableId,
          columns: selected,
        },
        { headers, responseType: 'blob' }
      );
    },
  });

  const handleDownload = async () => {
    try {
      const res = await exportMutation.mutateAsync();
      const fallback = `${(selectedTable?.name || 'tabla').replace(/[^\w.-]+/g, '_')}-plantilla.xlsx`;
      const filename = filenameFromDisposition(res.headers?.['content-disposition'], fallback);
      const blob = new Blob([res.data], {
        type:
          res.headers?.['content-type'] ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      downloadBlob(blob, filename);
      showSuccess('Plantilla descargada');
      onClose?.();
    } catch (err) {
      showError(await errorMessageFromAxios(err));
    }
  };

  const busy = exportMutation.isPending;
  const canDownload = Boolean(tableId && selected.length > 0 && !busy);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Exportar plantilla</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Elige una tabla del esquema escaneado y las columnas que quieres en el Excel.
        </Typography>
        <Autocomplete
          size="small"
          options={tables}
          value={selectedTable}
          onChange={(_, option) => handleTableChange(option?.id)}
          getOptionLabel={(t) => t?.id || ''}
          isOptionEqualToValue={(a, b) => a?.id === b?.id}
          disabled={busy || tables.length === 0}
          renderInput={(params) => (
            <TextField {...params} label="Tabla" placeholder="Buscar tabla…" />
          )}
        />
        {tables.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Primero pulsa Actualizar esquema para cargar las tablas.
          </Typography>
        ) : selectedTable ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ flex: 1 }}>
                Columnas ({selected.length}/{columns.length})
              </Typography>
              <FormControlLabel
                sx={{ mr: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={selected.length > 0 && !allSelected}
                    onChange={() =>
                      setSelected(allSelected ? [] : columns.map((c) => c.name))
                    }
                    disabled={busy || columns.length === 0}
                  />
                }
                label="Seleccionar todas"
              />
            </Box>
            <List
              dense
              disablePadding
              sx={{
                maxHeight: 280,
                overflow: 'auto',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              {columns.map((col) => (
                <ListItem key={col.name} disablePadding>
                  <ListItemButton onClick={() => toggleColumn(col.name)} disabled={busy} dense>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox
                        edge="start"
                        size="small"
                        checked={selected.includes(col.name)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={col.name}
                      secondary={col.dataType || null}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Selecciona una tabla para ver sus columnas.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
          onClick={handleDownload}
          disabled={!canDownload}
        >
          Descargar .xlsx
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExcelTemplateExportDialog;
